/**
 * Harvest Foreman PDF Generation Utility
 * Generates formatted PDF reports for "Form Potong Buah" based on foreman data.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { HarvestForemanRow } from '../types';
import { trackDownload } from '../../../activity';

/**
 * Format number with thousand separator
 */
const formatNumber = (num: number) => {
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(num);
};

/**
 * Format decimal with 2 decimal places
 */
const formatDecimal = (num?: number | null) => {
    if (num === undefined || num === null) return '0,00';
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num);
};

/**
 * Draw Header and Signatures on each page
 */
const drawHeader = (doc: jsPDF, foreman: HarvestForemanRow, pageNumber: number, totalPages: number) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margins = { top: 10, bottom: 10, left: 10, right: 10 };
    
    // Reset graphics state at the start of header drawing for each page
    doc.setLineWidth(0.3);
    doc.setDrawColor(0, 0, 0);
    
    // Header section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('FORM POTONG BUAH - SAKTI', margins.left, margins.top + 5);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');

    // Metadata Section (Left Aligned)
    let leftMetaY = margins.top + 12; // Reduced from +20 to move up
    const metaLineHeight = 4;
    const labelX = margins.left;
    const valueX = margins.left + 25;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Divisi', labelX, leftMetaY);
    doc.text(`: ${foreman.division}`, valueX, leftMetaY);
    leftMetaY += metaLineHeight;

    doc.text('Kemandoran', labelX, leftMetaY);
    doc.text(`: ${foreman.foreman_group_code}`, valueX, leftMetaY);
    leftMetaY += metaLineHeight;

    doc.text('Mandor', labelX, leftMetaY);
    doc.text(`: ${foreman.foreman_group_name}`, valueX, leftMetaY);
    leftMetaY += metaLineHeight;

    doc.setFont('helvetica', 'normal');
    doc.text('Tanggal Panen', labelX, leftMetaY);
    doc.text(`: ${format(new Date(foreman.rkh_date), 'dd-MM-yyyy')}`, valueX, leftMetaY);
    leftMetaY += metaLineHeight;

    doc.text('Tanggal Cetak', labelX, leftMetaY);
    doc.text(`: ${format(new Date(), 'dd-MM-yyyy HH:mm')}`, valueX, leftMetaY);
    leftMetaY += metaLineHeight;
    
    doc.text('Halaman', labelX, leftMetaY);
    doc.text(`: ${pageNumber} / ${totalPages}`, valueX, leftMetaY);

    // Signature section (Top Right)
    const sigY = margins.top + 5;
    const totalSigWidth = 110; // Increased to 110mm to avoid overlap
    const sigBoxWidth = totalSigWidth / 4;
    const sigBoxHeight = 26; 
    const sigStartX = pageWidth - margins.right - totalSigWidth;
    
    const sigs = [
        'DIBUAT OLEH\nKERANI BUAH',
        'DIPERIKSA OLEH\nMANDOR 1',
        'DIPERIKSA OLEH\nASISTEN DIVISI',
        'DISETUJUI OLEH\nMANAGER / ASKEP'
    ];

    doc.setFontSize(6);
    
    sigs.forEach((title, i) => {
        const x = sigStartX + (i * sigBoxWidth);
        
        // AGGRESSIVE: Reset lineWidth before EACH shape
        doc.setLineWidth(0.3);
        doc.setDrawColor(0, 0, 0);
        
        // Outer box
        doc.rect(x, sigY, sigBoxWidth, sigBoxHeight);
        
        // Split title into status and role
        const titleLines = title.split('\n');
        const statusText = titleLines[0];
        const roleText = titleLines.slice(1).join('\n');

        // Status Section
        doc.setFont('helvetica', 'normal');
        doc.text(statusText, x + (sigBoxWidth / 2), sigY + 3, { align: 'center' });
        
        // Line between Status and Role
        const statusLineY = sigY + 4.5;
        doc.setLineWidth(0.3);
        doc.line(x, statusLineY, x + sigBoxWidth, statusLineY);

        // Role Section
        doc.setFont('helvetica', 'normal');
        doc.text(roleText, x + (sigBoxWidth / 2), statusLineY + 3.5, { align: 'center', maxWidth: sigBoxWidth - 2 });

        // Bottom Grid Line (Tgl only)
        const rowHeight = 4;
        const lineTglY = sigY + sigBoxHeight - rowHeight;
        
        doc.setLineWidth(0.3);
        doc.line(x, lineTglY, x + sigBoxWidth, lineTglY);

        // Labels
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(5);
        doc.text('Nama', x + 1, lineTglY - 1); 
        doc.text('Tgl', x + 1, lineTglY + 3);
        doc.setFontSize(6); 
    });
};

/**
 * Generate PDF for Form Potong Buah
 */
export const generateHarvestForemanPDF = (foreman: HarvestForemanRow) => {
    // Portrait orientation to fit standard printing
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margins = { top: 10, bottom: 10, left: 10, right: 10 };
    
    // Table Data
    const tableBody: any[] = [];
    let globalIndex = 1;

    // Group details by harvester
    const groupedDetails: { [key: string]: typeof foreman.tph_details } = {};
    const harvesterOrder: string[] = [];

    (foreman.tph_details || []).forEach((detail) => {
        const key = `${detail.harvester_name || '-'}_${detail.harvester_nip || '-'}`;
        if (!groupedDetails[key]) {
            groupedDetails[key] = [];
            harvesterOrder.push(key);
        }
        groupedDetails[key].push(detail);
    });

    harvesterOrder.forEach((key) => {
        const details = groupedDetails[key];
        let harvesterHa = 0;
        let harvesterJjg = 0;
        let harvesterMatang = 0;
        let harvesterMentah = 0;
        let harvesterBusuk = 0;
        let harvesterAbnormal = 0;
        let harvesterTangkaiPanjang = 0;
        let harvesterBrondolan = 0;
        let name = '-';

        details.forEach((detail) => {
            harvesterHa += detail.ha_per_tph || 0;
            harvesterJjg += detail.bunch_qty || 0;
            harvesterMatang += detail.ripe_qty || 0;
            harvesterMentah += detail.unripe_qty || 0;
            harvesterBusuk += detail.rotten_empty_bunch_qty || 0;
            const abnormalSum = (detail.abnormal_01_qty || 0) + 
                                (detail.abnormal_02_qty || 0) + 
                                (detail.abnormal_03_qty || 0) + 
                                (detail.abnormal_04_qty || 0);
            harvesterAbnormal += abnormalSum;
            harvesterTangkaiPanjang += detail.abnormal_05_qty || 0;
            harvesterBrondolan += detail.loose_fruit_qty || 0;
            name = detail.harvester_name || '-';

            tableBody.push([
                globalIndex++,
                `${detail.harvester_name || '-'} / ${detail.harvester_nip || '-'}`,
                detail.bcc_number || '-',
                detail.tph_code || '-',
                detail.ha_per_tph ? formatDecimal(detail.ha_per_tph) : '-',
                detail.bunch_qty || '-',
                detail.ripe_qty || '-',
                detail.unripe_qty || '-',
                detail.rotten_empty_bunch_qty || '-',
                detail.loose_fruit_qty ? formatDecimal(detail.loose_fruit_qty) : '-',
                abnormalSum || '-',
                detail.abnormal_05_qty || '-',
                detail.no_spb || '-',
                detail.ticket_no || '-'
            ]);
        });

        // Add subtotal row for this harvester
        tableBody.push([
            '',
            'Total',
            '',
            '',
            formatDecimal(harvesterHa),
            formatNumber(harvesterJjg),
            formatNumber(harvesterMatang),
            formatNumber(harvesterMentah),
            formatNumber(harvesterBusuk),
            formatDecimal(harvesterBrondolan),
            formatNumber(harvesterAbnormal),
            formatNumber(harvesterTangkaiPanjang),
            '',
            ''
        ]);
    });

    // Calculate totals
    const totals = (foreman.tph_details || []).reduce((acc, curr) => ({
        ha: acc.ha + (curr.ha_per_tph || 0),
        jjg: acc.jjg + (curr.bunch_qty || 0),
        matang: acc.matang + (curr.ripe_qty || 0),
        mentah: acc.mentah + (curr.unripe_qty || 0),
        busuk: acc.busuk + (curr.rotten_empty_bunch_qty || 0),
        abnormal: acc.abnormal + ((curr.abnormal_01_qty || 0) + (curr.abnormal_02_qty || 0) + (curr.abnormal_03_qty || 0) + (curr.abnormal_04_qty || 0)),
        tangkai: acc.tangkai + (curr.abnormal_05_qty || 0),
        brondolan: acc.brondolan + (curr.loose_fruit_qty || 0)
    }), { ha: 0, jjg: 0, matang: 0, mentah: 0, busuk: 0, abnormal: 0, tangkai: 0, brondolan: 0 });

    // Group totals by block_code for RINGKASAN TOTAL
    const blockTotals: { [blockCode: string]: { ha: number; jjg: number; matang: number; mentah: number; busuk: number; abnormal: number; tangkai: number; brondolan: number } } = {};
    const blockOrder: string[] = [];

    (foreman.tph_details || []).forEach((detail) => {
        const blockCode = detail.block_code || '-';
        if (!blockTotals[blockCode]) {
            blockTotals[blockCode] = { ha: 0, jjg: 0, matang: 0, mentah: 0, busuk: 0, abnormal: 0, tangkai: 0, brondolan: 0 };
            blockOrder.push(blockCode);
        }
        blockTotals[blockCode].ha += detail.ha_per_tph || 0;
        blockTotals[blockCode].jjg += detail.bunch_qty || 0;
        blockTotals[blockCode].matang += detail.ripe_qty || 0;
        blockTotals[blockCode].mentah += detail.unripe_qty || 0;
        blockTotals[blockCode].busuk += detail.rotten_empty_bunch_qty || 0;
        blockTotals[blockCode].abnormal += ((detail.abnormal_01_qty || 0) + (detail.abnormal_02_qty || 0) + (detail.abnormal_03_qty || 0) + (detail.abnormal_04_qty || 0));
        blockTotals[blockCode].tangkai += detail.abnormal_05_qty || 0;
        blockTotals[blockCode].brondolan += detail.loose_fruit_qty || 0;
    });

    const ringkasanBody = blockOrder.map((blockCode) => [
        blockCode,
        formatDecimal(blockTotals[blockCode].ha),
        formatNumber(blockTotals[blockCode].jjg),
        formatNumber(blockTotals[blockCode].matang),
        formatNumber(blockTotals[blockCode].mentah),
        formatNumber(blockTotals[blockCode].busuk),
        formatDecimal(blockTotals[blockCode].brondolan),
        formatNumber(blockTotals[blockCode].abnormal),
        formatNumber(blockTotals[blockCode].tangkai)
    ]);

    // Add TOTAL row at the bottom of the Ringkasan table
    ringkasanBody.push([
        'TOTAL',
        formatDecimal(totals.ha),
        formatNumber(totals.jjg),
        formatNumber(totals.matang),
        formatNumber(totals.mentah),
        formatNumber(totals.busuk),
        formatDecimal(totals.brondolan),
        formatNumber(totals.abnormal),
        formatNumber(totals.tangkai)
    ]);

    // Define widths for the 14 columns (Total Usable Width: 190mm)
    const wNo = 6;
    const wPemanen = 30; 
    const wBcc = 26;
    const wTph = 15;
    const wHa = 8;
    const wJjg = 6;
    const wMatang = 8;
    const wMentah = 8;
    const wBusuk = 8;
    const wBron = 8;
    const wAbnormal = 10;
    const wTangkai = 10;
    const wSpb = 26;
    const wTicket = 21;
    // Total: 6 + 30 + 26 + 15 + 8 + 6 + 8 + 8 + 8 + 8 + 10 + 10 + 26 + 21 = 190mm

    autoTable(doc, {
        startY: 45, 
        margin: { top: 45, bottom: 20, left: margins.left },
        head: [
            [
                'NO', 
                'PEMANEN / NIP', 'NO. BCC',
                'TPH CODE', 'HA',
                'JJG',
                'MATANG',
                'MENTAH',
                'BUSUK',
                'KG\nBRD',
                'ABNORMAL',
                'TANGKAI\nPANJANG',
                'NO SPB', 'TICKET WB'
            ]
        ],
        body: tableBody,
        theme: 'grid',
        styles: {
            fontSize: 6, 
            cellPadding: 1,
            halign: 'center',
            valign: 'middle',
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            textColor: [0, 0, 0]
        },
        headStyles: {
            fillColor: [245, 245, 245],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            lineWidth: 0.1,
            fontSize: 4.5,
            cellPadding: 0.3
        },
        columnStyles: {
            0: { halign: 'right', cellWidth: wNo },
            1: { halign: 'left', cellWidth: wPemanen },
            2: { halign: 'center', cellWidth: wBcc },
            3: { halign: 'center', cellWidth: wTph },
            4: { halign: 'right', cellWidth: wHa },
            5: { halign: 'right', cellWidth: wJjg },
            6: { halign: 'right', cellWidth: wMatang },
            7: { halign: 'right', cellWidth: wMentah },
            8: { halign: 'right', cellWidth: wBusuk },
            9: { halign: 'right', cellWidth: wBron },
            10: { halign: 'right', cellWidth: wAbnormal },
            11: { halign: 'right', cellWidth: wTangkai },
            12: { halign: 'center', cellWidth: wSpb },
            13: { halign: 'center', cellWidth: wTicket }
        },
        didParseCell: (data) => {
            if (data.row.section === 'body') {
                const rawRow = data.row.raw as any;
                const isSubtotalRow = rawRow && rawRow[1] && String(rawRow[1]) === 'Total';
                if (isSubtotalRow) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            }
        }
    });

    let finalY = (doc as any).lastAutoTable.finalY + 10;

    // Footer dimensions
    const boxHeight = 32;
    const boxWidth = 42; // Reduced from 55 to give more space to Ringkasan table
    const footerGap = 4;
    const ringkasanTableHeight = (ringkasanBody.length + 1) * 5;
    const footerRequiredHeight = Math.max(boxHeight, ringkasanTableHeight) + 10;

    // Check if Footer fits on the current page
    if (finalY + footerRequiredHeight > pageHeight - 20) {
        doc.addPage();
        finalY = 60; // Start after header area on new page
    }

    const footerY = finalY;

    // 1. KETERANGAN (Left Side)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('KETERANGAN', margins.left, footerY);
    doc.setLineWidth(0.3);
    doc.setDrawColor(0, 0, 0);
    doc.rect(margins.left, footerY + 2, boxWidth, boxHeight);
    
    doc.setFontSize(5.5); // Slightly smaller font for legend to fit smaller box
    doc.setFont('helvetica', 'normal');
    const ketX = margins.left + 2;
    let ketY = footerY + 7;
    doc.text('JJG  : Janjang', ketX, ketY); ketY += 4;
    doc.text('TPH  : Tempat Pengumpulan Hasil', ketX, ketY); ketY += 4;
    doc.text('SPB  : Surat Pengantar Buah', ketX, ketY); ketY += 4;
    doc.text('BCC  : Bunch Count Chit', ketX, ketY); ketY += 4;
    doc.text('NIP  : Nomor Induk Pegawai', ketX, ketY); ketY += 4;
    doc.text('WB  : Weight Bridge', ketX, ketY);

    // 2. RINGKASAN TOTAL (Center)
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('RINGKASAN TOTAL', pageWidth / 2, footerY, { align: 'center' });
    
    autoTable(doc, {
        startY: footerY + 2,
        margin: { 
            left: margins.left + boxWidth + footerGap, 
            right: margins.right + boxWidth + footerGap 
        },
        head: [['BLOK', 'LUAS\n(HA)', 'TOTAL\nJJG', 'TOTAL\nMATANG', 'TOTAL\nMENTAH', 'TOTAL\nBUSUK', 'TOTAL KG\nBRD', 'TOTAL\nABNORMAL', 'TOTAL TANGKAI\nPANJANG']],
        body: ringkasanBody,
        theme: 'grid',
        styles: {
            fontSize: 5.5, // Reduced font size to fit columns beautifully
            cellPadding: 0.8,
            halign: 'center',
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            textColor: [0, 0, 0]
        },
        headStyles: {
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 5
        },
        didParseCell: (data) => {
            if (data.row.section === 'body') {
                const rawRow = data.row.raw as any;
                if (rawRow && rawRow[0] === 'TOTAL') {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            }
        }
    });

    // 3. CATATAN (Right Side)
    const catatanX = pageWidth - margins.right - boxWidth;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('CATATAN', catatanX, footerY);
    doc.setLineWidth(0.3);
    doc.setDrawColor(0, 0, 0);
    doc.rect(catatanX, footerY + 2, boxWidth, boxHeight);
    
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    const catContentX = catatanX + 2;
    let catContentY = footerY + 7;
    doc.text('• Data diambil dari sistem SAKTI.', catContentX, catContentY); catContentY += 3.5;
    doc.text('• Pastikan kesesuaian data sebelum', catContentX, catContentY); catContentY += 3.5;
    doc.text('  input to Odoo.', catContentX, catContentY);

    // Final Pass: Add headers and page numbers to all pages
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        // FORCE state immediately after switching page
        doc.setLineWidth(0.3);
        doc.setDrawColor(0, 0, 0);
        drawHeader(doc, foreman, i, totalPages);
    }

    return doc;
};

/**
 * Download Harvest Foreman PDF
 */
export const downloadHarvestForemanPDF = (foreman: HarvestForemanRow) => {
    const doc = generateHarvestForemanPDF(foreman);
    const filename = `Form_Potong_Buah_${foreman.foreman_group_code}_${format(new Date(foreman.rkh_date), 'yyyyMMdd')}.pdf`;
    doc.save(filename);
    trackDownload('Form Potong Buah', { filename });
};
